(() => {
  'use strict';

  if (window.HideawayAnalytics) return;

  const COMMON_REQUIRED = Object.freeze([
    'event_id',
    'event_version',
    'occurred_at',
    'environment',
    'session_id',
    'page_type',
    'page_path',
    'device_category',
    'source_surface'
  ]);

  const EVENT_REQUIRED = Object.freeze({
    hh_owned_family_module_viewed: ['module_id', 'module_position', 'family_count'],
    hh_shop_hideaway_selected: ['entry_location', 'destination_path'],
    hh_family_selected: ['family_key', 'entry_location', 'destination_path', 'tile_position'],
    hh_product_detail_viewed: ['product_id', 'variant_id', 'commercial_model', 'family_key', 'collection_context'],
    hh_search_results_viewed: ['search_id', 'search_surface', 'result_state', 'result_count_bucket', 'query_class', 'query_length_bucket'],
    hh_search_result_selected: ['search_id', 'result_type', 'result_position', 'family_key'],
    hh_search_recovery_selected: ['search_id', 'recovery_type', 'destination_path'],
    hh_collection_filter_changed: ['collection_handle', 'filter_action', 'filter_group', 'filter_value_key', 'active_filter_count', 'result_count_bucket'],
    hh_variant_stockout_viewed: ['product_id', 'variant_id', 'family_key', 'size_key', 'color_key', 'stockout_trigger'],
    hh_notify_me_opened: ['product_id', 'variant_id', 'family_key', 'size_key', 'color_key', 'stockout_trigger', 'notify_channel_options'],
    hh_notify_me_request_succeeded: ['product_id', 'variant_id', 'family_key', 'size_key', 'color_key', 'stockout_trigger', 'notify_channel'],
    hh_notify_me_request_failed: ['product_id', 'variant_id', 'family_key', 'size_key', 'color_key', 'stockout_trigger', 'notify_channel', 'failure_category'],
    hh_cart_item_added: ['product_id', 'variant_id', 'family_key', 'commercial_model', 'quantity', 'unit_price_minor', 'currency', 'cart_source'],
    hh_pickup_option_selected: ['fulfillment_method', 'pickup_location_key', 'checkout_stage', 'item_count_bucket', 'cart_value_bucket', 'currency', 'contains_owned_product'],
    hh_checkout_handoff_started: ['handoff_source', 'item_count_bucket', 'cart_value_bucket', 'currency', 'contains_owned_product', 'owned_item_count_bucket']
  });

  const CONDITIONAL = Object.freeze({
    hh_search_result_selected: ['product_id'],
    hh_variant_stockout_viewed: ['sku'],
    hh_notify_me_opened: ['sku'],
    hh_notify_me_request_succeeded: ['sku'],
    hh_notify_me_request_failed: ['sku'],
    hh_cart_item_added: ['sku']
  });

  const FORBIDDEN = new Set([
    'name', 'email', 'phone', 'address', 'postal_code', 'location',
    'account_id', 'customer_id', 'loyalty_id', 'user_id', 'raw_query',
    'query_text', 'query_hash', 'full_url', 'checkout_url', 'checkout_token',
    'cart_token', 'payment_method', 'payment_status', 'card_data',
    'billing_data', 'order_notes', 'discount_code', 'error_message',
    'ip_address', 'user_agent', 'advertising_id', 'subscriber_id',
    'provider_profile_id'
  ]);

  const ENUMS = Object.freeze({
    environment: ['test', 'preview', 'production'],
    page_type: ['home', 'collection', 'product', 'search', 'cart', 'checkout', 'other'],
    device_category: ['mobile', 'tablet', 'desktop', 'unknown'],
    source_surface: [
      'home_owned_family_module', 'home_video_header', 'shop_hideaway_hub',
      'approved_submenu', 'footer', 'search_drawer', 'search_page',
      'collection_filter', 'product_page', 'quick_add', 'quick_view',
      'recommendation', 'collection_card', 'search_result', 'cart_drawer',
      'cart_page', 'checkout', 'notify_me', 'external_adapter', 'other'
    ],
    family_key: [
      'aaron_kai', 'hilo_varsity_crewneck', 'hideaway_evry',
      'hideaway_headwear', 'mahina', 'other_owned', 'wholesale', 'unknown'
    ],
    commercial_model: ['owned', 'wholesale', 'not_merchandise', 'unknown'],
    entry_location: [
      'home_video_header', 'home_owned_family_module', 'shop_hideaway_hub',
      'approved_submenu', 'footer', 'search_recovery',
      'collection_cross_link', 'other'
    ],
    search_surface: ['drawer', 'search_page'],
    result_state: ['results', 'no_results', 'error'],
    result_count_bucket: ['0', '1', '2_5', '6_10', '11_25', '26_plus', 'unknown'],
    query_class: ['aaron_kai', 'crewneck', 'hideaway_evry', 'headwear', 'mahina', 'hideaway_brand', 'other'],
    query_length_bucket: ['1_3', '4_10', '11_25', '26_plus'],
    result_type: ['product', 'collection', 'page', 'other'],
    recovery_type: ['shop_hideaway', 'family', 'mahina', 'best_seller', 'contact', 'clear_query', 'other'],
    filter_action: ['applied', 'removed', 'cleared_all'],
    filter_group: ['availability', 'price', 'size', 'product_type', 'color', 'fit', 'headwear_profile', 'closure', 'jewelry_type', 'material', 'finish', 'other'],
    stockout_trigger: ['initial_variant', 'shopper_selected_variant', 'cart_rejection'],
    failure_category: ['client_validation', 'provider_rejected', 'network', 'unknown'],
    cart_source: ['product_page', 'quick_add', 'quick_view', 'recommendation', 'collection_card', 'search_result', 'other'],
    fulfillment_method: ['pickup'],
    pickup_location_key: ['hideaway_hilo'],
    handoff_source: ['cart_drawer', 'cart_page', 'buy_now', 'other'],
    item_count_bucket: ['0', '1', '2_3', '4_6', '7_plus'],
    owned_item_count_bucket: ['0', '1', '2_3', '4_6', '7_plus'],
    cart_value_bucket: ['under_50', '50_99', '100_199', '200_plus', 'unknown'],
    notify_channel: ['email', 'sms', 'unknown']
  });

  const NUMBER_FIELDS = new Set([
    'event_version', 'module_position', 'family_count', 'tile_position',
    'result_position', 'active_filter_count', 'quantity', 'unit_price_minor'
  ]);
  const BOOLEAN_FIELDS = new Set(['contains_owned_product']);
  const SAFE_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
  const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const APPROVED_SKU_PATTERN = /^HH-[A-Z0-9]+(?:-[A-Z0-9]+)+$/;
  const sentKeys = new Set();
  const searchStates = new Map();
  const stockoutKeys = new Set();
  let pendingCartContext = null;
  let context = {};
  let moduleObserver = null;

  function readContext() {
    const element = document.getElementById('HHAnalyticsContext');
    if (!element) return {};
    try {
      return JSON.parse(element.textContent);
    } catch (_error) {
      return {};
    }
  }

  function uuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto?.getRandomValues?.(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_error) {
      return false;
    }
  }

  function sessionId() {
    const key = 'hh_analytics_session_v1';
    try {
      let value = window.sessionStorage.getItem(key);
      if (!value) {
        value = uuid();
        window.sessionStorage.setItem(key, value);
      }
      return value;
    } catch (_error) {
      return uuid();
    }
  }

  function environment() {
    if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) return 'test';
    const previewQuery = new URLSearchParams(window.location.search).has('preview_theme_id');
    const themeRole = window.Shopify?.theme?.role;
    if (previewQuery || themeRole === 'unpublished' || document.body.classList.contains('shopify-design-mode')) return 'preview';
    return 'production';
  }

  function deviceCategory() {
    if (!window.matchMedia) return 'unknown';
    if (window.matchMedia('(max-width: 767px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 1067px)').matches) return 'tablet';
    return 'desktop';
  }

  function pagePath(value = window.location.pathname) {
    try {
      const parsed = new URL(value, window.location.origin);
      return parsed.pathname || '/';
    } catch (_error) {
      return '/';
    }
  }

  function commonEnvelope(sourceSurface) {
    return {
      event_id: uuid(),
      event_version: 1,
      occurred_at: new Date().toISOString(),
      environment: environment(),
      session_id: sessionId(),
      page_type: context.pageType || 'other',
      page_path: pagePath(),
      device_category: deviceCategory(),
      source_surface: sourceSurface
    };
  }

  function validateValue(key, value) {
    if (value === undefined || value === null || value === '') return `${key} is required`;
    if (NUMBER_FIELDS.has(key) && (!Number.isInteger(value) || value < 0)) return `${key} must be a non-negative integer`;
    if (BOOLEAN_FIELDS.has(key) && typeof value !== 'boolean') return `${key} must be boolean`;
    if (ENUMS[key] && !ENUMS[key].includes(value)) return `${key} is outside its controlled vocabulary`;
    if (key === 'event_id' && !UUID_PATTERN.test(value)) return 'event_id must be a UUID v4';
    if (key === 'event_version' && value !== 1) return 'event_version must be 1';
    if (key === 'occurred_at' && Number.isNaN(Date.parse(value))) return 'occurred_at must be ISO-8601';
    if (key === 'page_path' || key === 'destination_path') {
      if (typeof value !== 'string' || !value.startsWith('/') || value.includes('?') || value.includes('#') || value.includes('://')) {
        return `${key} must be a path without origin, query, or fragment`;
      }
    }
    if (['filter_value_key', 'size_key', 'color_key', 'collection_handle', 'collection_context', 'checkout_stage'].includes(key)) {
      if (typeof value !== 'string' || !SAFE_KEY_PATTERN.test(value)) return `${key} must be a controlled key`;
    }
    if (key === 'notify_channel_options') {
      if (!Array.isArray(value) || value.length === 0 || value.some((item) => !['email', 'sms'].includes(item))) {
        return 'notify_channel_options must contain approved channel keys';
      }
    }
    if (key === 'sku' && !APPROVED_SKU_PATTERN.test(value)) return 'sku is not an approved permanent SKU';
    return null;
  }

  function validate(name, payload) {
    if (!EVENT_NAME_PATTERN.test(name) || !name.startsWith('hh_') || !EVENT_REQUIRED[name]) {
      return { ok: false, errors: ['event name is not in the v1 contract'] };
    }
    const allowed = new Set([...COMMON_REQUIRED, ...EVENT_REQUIRED[name], ...(CONDITIONAL[name] || [])]);
    const errors = [];
    Object.keys(payload).forEach((key) => {
      if (FORBIDDEN.has(key)) errors.push(`${key} is forbidden`);
      if (!allowed.has(key)) errors.push(`${key} is not allowed for ${name}`);
    });
    [...COMMON_REQUIRED, ...EVENT_REQUIRED[name]].forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) errors.push(`${key} is required`);
    });
    Object.entries(payload).forEach(([key, value]) => {
      const error = validateValue(key, value);
      if (error) errors.push(error);
    });
    return { ok: errors.length === 0, errors };
  }

  function emit(name, properties, options = {}) {
    if (!analyticsAllowed()) return { ok: false, reason: 'analytics_consent_not_allowed' };
    if (options.dedupeKey && sentKeys.has(options.dedupeKey)) return { ok: false, reason: 'duplicate_suppressed' };

    const payload = { ...commonEnvelope(options.sourceSurface || 'other'), ...properties };
    const validation = validate(name, payload);
    if (!validation.ok) {
      document.dispatchEvent(new CustomEvent('hh:analytics-rejected', {
        detail: { eventName: name, errors: validation.errors }
      }));
      return { ok: false, reason: 'contract_rejected', errors: validation.errors };
    }

    if (options.dedupeKey) sentKeys.add(options.dedupeKey);
    document.dispatchEvent(new CustomEvent('hh:analytics-event', {
      detail: { eventName: name, payload }
    }));

    try {
      const result = window.Shopify?.analytics?.publish?.(name, payload);
      if (result?.catch) {
        result.catch(() => {
          document.dispatchEvent(new CustomEvent('hh:analytics-publish-failed', {
            detail: { eventName: name, failureCategory: 'shopify_event_bus' }
          }));
        });
      }
    } catch (_error) {
      document.dispatchEvent(new CustomEvent('hh:analytics-publish-failed', {
        detail: { eventName: name, failureCategory: 'shopify_event_bus' }
      }));
    }
    return { ok: true, payload };
  }

  function resultCountBucket(count) {
    if (!Number.isInteger(count) || count < 0) return 'unknown';
    if (count === 0) return '0';
    if (count === 1) return '1';
    if (count <= 5) return '2_5';
    if (count <= 10) return '6_10';
    if (count <= 25) return '11_25';
    return '26_plus';
  }

  function itemCountBucket(count) {
    if (!Number.isInteger(count) || count < 0) return null;
    if (count <= 0) return '0';
    if (count === 1) return '1';
    if (count <= 3) return '2_3';
    if (count <= 6) return '4_6';
    return '7_plus';
  }

  function cartValueBucket(minor) {
    if (!Number.isFinite(minor) || minor < 0) return 'unknown';
    if (minor < 5000) return 'under_50';
    if (minor < 10000) return '50_99';
    if (minor < 20000) return '100_199';
    return '200_plus';
  }

  function queryClass(query) {
    const value = String(query || '').trim().toLowerCase();
    if (/\baaron\s*kai\b/.test(value)) return 'aaron_kai';
    if (/\bcrew\s*neck\b|\bvarsity\b/.test(value)) return 'crewneck';
    if (/\bevry\b/.test(value)) return 'hideaway_evry';
    if (/\bhat\b|\bcap\b|\bheadwear\b|\btrucker\b/.test(value)) return 'headwear';
    if (/\bmahina\b/.test(value)) return 'mahina';
    if (/\bhideaway\b|\bhilo\b/.test(value)) return 'hideaway_brand';
    return 'other';
  }

  function queryLengthBucket(query) {
    const length = String(query || '').trim().length;
    if (length <= 3) return '1_3';
    if (length <= 10) return '4_10';
    if (length <= 25) return '11_25';
    return '26_plus';
  }

  function normalizeFamily(value, commercialModel = 'unknown') {
    const approved = ENUMS.family_key;
    if (approved.includes(value)) return value;
    if (commercialModel === 'wholesale') return 'wholesale';
    if (commercialModel === 'owned') return 'other_owned';
    return 'unknown';
  }

  function normalizeCommercialModel(value) {
    return ENUMS.commercial_model.includes(value) ? value : 'unknown';
  }

  function normalizeOption(value, type) {
    const normalized = String(value || '').trim().toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (!normalized) return 'unknown';
    if (type === 'size') {
      const sizes = new Set(['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', '3xl', 'one_size']);
      if (sizes.has(normalized)) return normalized;
      if (/^\d{1,2}(?:_\d{1,2})?$/.test(normalized)) return `size_${normalized}`;
      return 'unknown';
    }
    const colors = new Set([
      'black', 'white', 'gray', 'grey', 'brown', 'tan', 'beige', 'cream',
      'red', 'orange', 'yellow', 'green', 'blue', 'navy', 'purple', 'pink',
      'gold', 'silver', 'multi'
    ]);
    return colors.has(normalized) ? normalized.replace('grey', 'gray') : 'unknown';
  }

  function optionKeys(optionNames = [], optionValues = []) {
    let size = 'unknown';
    let color = 'unknown';
    optionNames.forEach((name, index) => {
      const key = String(name || '').toLowerCase();
      if (key.includes('size')) size = normalizeOption(optionValues[index], 'size');
      if (key.includes('color') || key.includes('colour')) color = normalizeOption(optionValues[index], 'color');
    });
    return { size_key: size, color_key: color };
  }

  function approvedSku(value) {
    return APPROVED_SKU_PATTERN.test(String(value || '')) ? value : undefined;
  }

  function trackSearchState({ searchSurface, resultState, resultCount, query }) {
    const searchId = uuid();
    const result = emit('hh_search_results_viewed', {
      search_id: searchId,
      search_surface: searchSurface,
      result_state: resultState,
      result_count_bucket: resultCountBucket(resultCount),
      query_class: queryClass(query),
      query_length_bucket: queryLengthBucket(query)
    }, {
      sourceSurface: searchSurface === 'drawer' ? 'search_drawer' : 'search_page'
    });
    if (result.ok) searchStates.set(searchSurface, { searchId });
    return result.ok ? searchId : null;
  }

  function resultType(link) {
    if (link.closest('product-card')) return 'product';
    if (link.classList.contains('thb-predictive-search--collection')) return 'collection';
    if (link.classList.contains('thb-predictive-search--article')) return 'page';
    if (link.closest('.blog-post')) return 'page';
    return 'other';
  }

  function resultPosition(link, container) {
    const productCard = link.closest('product-card');
    if (productCard) {
      return Math.max(1, Array.from(container.querySelectorAll('product-card')).indexOf(productCard) + 1);
    }
    const links = Array.from(container.querySelectorAll('a[href]')).filter((item) => {
      return item.matches('.thb-predictive-search--collection, .thb-predictive-search--article, .thb-predictive-search--query') || item.closest('.blog-post');
    });
    return Math.max(1, links.indexOf(link) + 1);
  }

  function sourceLocation(element) {
    if (element.closest('.section-owned-family-grid')) return 'home_owned_family_module';
    if (element.closest('slide-show, .slideshow')) return 'home_video_header';
    if (element.closest('.shopify-section-group-footer-group, footer')) return 'footer';
    if (element.closest('theme-header, header')) return 'approved_submenu';
    return 'other';
  }

  function captureCartSource(element) {
    const productForm = element.closest('product-form');
    const productCard = element.closest('product-card');
    let source = 'other';
    let data = {};

    if (productForm) {
      source = productForm.closest('#Product-Drawer') ? 'quick_view' : 'product_page';
      data = productForm.dataset;
    } else if (productCard) {
      if (productCard.closest('#Search-Drawer, .template-search')) source = 'search_result';
      else if (productCard.closest('product-recommendations')) source = 'recommendation';
      else if (element.closest('.product-card--add-to-cart-button-simple')) source = 'quick_add';
      else source = 'collection_card';
      data = productCard.dataset;
    }

    pendingCartContext = {
      expiresAt: Date.now() + 10000,
      cartSource: source,
      productId: data.hhProductId,
      variantId: data.hhVariantId,
      familyKey: data.hhFamilyKey,
      commercialModel: data.hhCommercialModel,
      sku: data.hhSku,
      unitPriceMinor: Number(data.hhUnitPriceMinor),
      currency: data.hhCurrency
    };
  }

  function trackConfirmedCartAdd(event) {
    const line = event.detail?.product;
    if (!line) return;
    const pending = pendingCartContext?.expiresAt > Date.now() ? pendingCartContext : {};
    pendingCartContext = null;
    const commercialModel = normalizeCommercialModel(pending.commercialModel || context.product?.commercialModel);
    const payload = {
      product_id: String(line.product_id || pending.productId || context.product?.productId || ''),
      variant_id: String(line.variant_id || line.id || pending.variantId || context.product?.variantId || ''),
      family_key: normalizeFamily(pending.familyKey || context.product?.familyKey, commercialModel),
      commercial_model: commercialModel,
      quantity: Number.parseInt(line.quantity, 10) || 1,
      unit_price_minor: Number.parseInt(line.final_price || line.price || pending.unitPriceMinor || context.product?.unitPriceMinor, 10),
      currency: String(pending.currency || context.currency || '').toUpperCase(),
      cart_source: pending.cartSource || 'other'
    };
    const sku = approvedSku(line.sku || pending.sku || context.product?.sku);
    if (sku) payload.sku = sku;
    emit('hh_cart_item_added', payload, { sourceSurface: payload.cart_source });
  }

  function trackStockout(variant, trigger, sourceElement) {
    if (!variant || variant.available !== false) return;
    const productData = sourceElement?.closest('product-form')?.dataset || {};
    const productId = String(productData.hhProductId || context.product?.productId || '');
    const variantId = String(variant.id || productData.hhVariantId || context.product?.variantId || '');
    const key = `${productId}:${variantId}`;
    if (stockoutKeys.has(key)) return;

    const commercialModel = normalizeCommercialModel(productData.hhCommercialModel || context.product?.commercialModel);
    const options = optionKeys(context.product?.optionNames, variant.options || context.product?.selectedOptions);
    const payload = {
      product_id: productId,
      variant_id: variantId,
      family_key: normalizeFamily(productData.hhFamilyKey || context.product?.familyKey, commercialModel),
      ...options,
      stockout_trigger: trigger
    };
    const sku = approvedSku(variant.sku || productData.hhSku || context.product?.sku);
    if (sku) payload.sku = sku;
    const result = emit('hh_variant_stockout_viewed', payload, {
      sourceSurface: 'product_page',
      dedupeKey: `stockout:${key}`
    });
    if (result.ok) stockoutKeys.add(key);
  }

  function filterEntries(params) {
    return Array.from(new URLSearchParams(params).entries())
      .filter(([key]) => key.startsWith('filter.'))
      .map(([key, value]) => `${key}=${value}`)
      .sort();
  }

  function filterGroup(key) {
    if (key.includes('availability')) return 'availability';
    if (key.includes('price')) return 'price';
    if (key.includes('normalized_size') || key.endsWith('.size')) return 'size';
    if (key.includes('normalized_color') || key.endsWith('.color')) return 'color';
    if (key.includes('product_type')) return 'product_type';
    if (key.includes('fit')) return 'fit';
    if (key.includes('headwear_profile')) return 'headwear_profile';
    if (key.includes('closure')) return 'closure';
    if (key.includes('jewelry_type')) return 'jewelry_type';
    if (key.includes('material')) return 'material';
    if (key.includes('finish')) return 'finish';
    return 'other';
  }

  function filterValueKey(key, value) {
    if (key.includes('availability')) return value === '1' ? 'available' : 'unknown';
    const normalized = String(value || '').trim().toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64);
    if (key.includes('normalized_size') || key.endsWith('.size')) return normalizeOption(normalized, 'size');
    if (key.includes('normalized_color') || key.endsWith('.color')) return normalizeOption(normalized, 'color');
    if (key.includes('product_type')) {
      const productTypes = new Set([
        'apparel_top', 'apparel_bottom', 'apparel_dress', 'apparel_outerwear',
        'apparel_activewear_top', 'apparel_activewear_bottom',
        'footwear_flip_flop', 'headwear_trucker_hat', 'headwear_dad_hat',
        'headwear_other', 'jewelry_bangle', 'jewelry_bracelet',
        'jewelry_cuff', 'jewelry_earrings', 'jewelry_necklace', 'swim_top',
        'swim_bottom', 'swim_one_piece', 'swim_boardshort', 'accessory_bag',
        'accessory_hair', 'accessory_sunglasses', 'accessory_towel',
        'accessory_sticker', 'accessory_other', 'unknown_merchandise'
      ]);
      return productTypes.has(normalized) ? normalized : 'unknown';
    }
    return 'unknown';
  }

  function visibleResultCount() {
    const text = document.getElementById('ProductCount')?.textContent || '';
    const match = text.replace(/,/g, '').match(/\d+/);
    if (match) return Number.parseInt(match[0], 10);
    return document.querySelectorAll('#product-grid > li').length;
  }

  function trackCollectionFilterChange({ previousSearchParams = '', searchParams = '' }) {
    if (context.pageType !== 'collection') return null;
    const before = filterEntries(previousSearchParams);
    const after = filterEntries(searchParams);
    if (before.join('&') === after.join('&')) return null;

    const added = after.filter((item) => !before.includes(item));
    const removed = before.filter((item) => !after.includes(item));
    let action = 'applied';
    let changed = added[0];
    if (before.length > 0 && after.length === 0) {
      action = 'cleared_all';
      changed = removed[0];
    } else if (!added.length && removed.length) {
      action = 'removed';
      changed = removed[0];
    }
    const [key = 'other', value = 'unknown'] = String(changed || '').split('=');
    return emit('hh_collection_filter_changed', {
      collection_handle: String(context.collectionHandle || 'unknown').replace(/[^a-z0-9_-]/g, '_').slice(0, 64) || 'unknown',
      filter_action: action,
      filter_group: filterGroup(key),
      filter_value_key: filterValueKey(key, value),
      active_filter_count: after.length,
      result_count_bucket: resultCountBucket(visibleResultCount())
    }, { sourceSurface: 'collection_filter' });
  }

  function setupModuleObserver() {
    if (moduleObserver) return;
    const module = document.querySelector('[data-hh-owned-family-module]');
    if (!module || !window.IntersectionObserver) return;
    let timer = null;
    moduleObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
        if (!timer) {
          timer = window.setTimeout(() => {
            emit('hh_owned_family_module_viewed', {
              module_id: module.dataset.hhModuleId,
              module_position: Number.parseInt(module.dataset.hhModulePosition, 10),
              family_count: Number.parseInt(module.dataset.hhFamilyCount, 10)
            }, {
              sourceSurface: 'home_owned_family_module',
              dedupeKey: 'module:home_owned_families_v1'
            });
          }, 1000);
        }
      } else if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    }, { threshold: [0, 0.5, 1] });
    moduleObserver.observe(module);
  }

  function trackInitialPageState() {
    setupModuleObserver();
    if (context.product) {
      const commercialModel = normalizeCommercialModel(context.product.commercialModel);
      emit('hh_product_detail_viewed', {
        product_id: String(context.product.productId),
        variant_id: String(context.product.variantId),
        commercial_model: commercialModel,
        family_key: normalizeFamily(context.product.familyKey, commercialModel),
        collection_context: String(context.product.collectionContext || 'unknown').replace(/[^a-z0-9_-]/g, '_').slice(0, 64) || 'unknown'
      }, {
        sourceSurface: 'product_page',
        dedupeKey: `pdp:${context.product.productId}:${context.product.variantId}`
      });
      if (context.product.available === false) {
        trackStockout({
          id: context.product.variantId,
          sku: context.product.sku,
          available: false,
          options: context.product.selectedOptions
        }, 'initial_variant');
      }
    }
    if (context.search?.performed && !searchStates.has('search_page')) {
      const query = document.querySelector('.template-search input[type="search"], main input[type="search"]')?.value || '';
      trackSearchState({
        searchSurface: 'search_page',
        resultState: context.search.resultCount > 0 ? 'results' : 'no_results',
        resultCount: Number.parseInt(context.search.resultCount, 10) || 0,
        query
      });
    }
  }

  function onDocumentClick(event) {
    const target = event.target.closest('a[href], button, [role="button"]');
    if (!target) return;
    if (
      target.matches('[type="submit"], [name="add"]') ||
      target.closest('.product-card--add-to-cart-button-simple, .product-card-sizes--size')
    ) {
      captureCartSource(target);
    }

    const familyLink = target.closest('[data-hh-family-link]');
    if (familyLink) {
      emit('hh_family_selected', {
        family_key: familyLink.dataset.hhFamilyKey,
        entry_location: familyLink.dataset.hhEntryLocation,
        destination_path: pagePath(familyLink.href),
        tile_position: Number.parseInt(familyLink.dataset.hhTilePosition, 10)
      }, { sourceSurface: 'home_owned_family_module' });
      return;
    }

    const recovery = target.closest('[data-hh-search-recovery]');
    if (recovery) {
      const surface = recovery.closest('#Search-Drawer') ? 'drawer' : 'search_page';
      const state = searchStates.get(surface);
      if (!state) return;
      emit('hh_search_recovery_selected', {
        search_id: state.searchId,
        recovery_type: recovery.dataset.hhRecoveryType || 'other',
        destination_path: pagePath(recovery.href || window.location.pathname)
      }, {
        sourceSurface: surface === 'drawer' ? 'search_drawer' : 'search_page'
      });
      return;
    }

    if (target.tagName === 'A' && pagePath(target.href) === '/collections/hideaway') {
      const entryLocation = sourceLocation(target);
      emit('hh_shop_hideaway_selected', {
        entry_location: entryLocation,
        destination_path: '/collections/hideaway'
      }, { sourceSurface: entryLocation });
      return;
    }

    const searchContainer = target.closest('#Search-Drawer .thb-predictive-search, .template-search #ProductGridContainer');
    if (searchContainer && target.tagName === 'A') {
      const surface = target.closest('#Search-Drawer') ? 'drawer' : 'search_page';
      const state = searchStates.get(surface);
      if (!state) return;
      const type = resultType(target);
      const card = target.closest('product-card');
      const payload = {
        search_id: state.searchId,
        result_type: type,
        result_position: resultPosition(target, searchContainer),
        family_key: normalizeFamily(card?.dataset.hhFamilyKey, normalizeCommercialModel(card?.dataset.hhCommercialModel))
      };
      if (type === 'product' && card?.dataset.hhProductId) payload.product_id = card.dataset.hhProductId;
      emit('hh_search_result_selected', payload, {
        sourceSurface: surface === 'drawer' ? 'search_drawer' : 'search_page'
      });
      return;
    }
  }

  function manualProductPayload(properties) {
    const commercialModel = normalizeCommercialModel(properties.commercial_model || context.product?.commercialModel);
    const payload = {
      product_id: String(properties.product_id || context.product?.productId || ''),
      variant_id: String(properties.variant_id || context.product?.variantId || ''),
      family_key: normalizeFamily(properties.family_key || context.product?.familyKey, commercialModel),
      size_key: properties.size_key || 'unknown',
      color_key: properties.color_key || 'unknown',
      stockout_trigger: properties.stockout_trigger || 'shopper_selected_variant'
    };
    const sku = approvedSku(properties.sku || context.product?.sku);
    if (sku) payload.sku = sku;
    return payload;
  }

  context = readContext();

  window.HideawayAnalytics = Object.freeze({
    version: 1,
    supportedEvents: Object.freeze(Object.keys(EVENT_REQUIRED)),
    validate,
    emit,
    trackSearchState,
    trackCollectionFilterChange,
    trackNotifyMeOpened(properties = {}) {
      return emit('hh_notify_me_opened', {
        ...manualProductPayload(properties),
        notify_channel_options: properties.notify_channel_options
      }, { sourceSurface: 'notify_me' });
    },
    trackNotifyMeRequestSucceeded(properties = {}) {
      return emit('hh_notify_me_request_succeeded', {
        ...manualProductPayload(properties),
        notify_channel: properties.notify_channel
      }, { sourceSurface: 'notify_me' });
    },
    trackNotifyMeRequestFailed(properties = {}) {
      return emit('hh_notify_me_request_failed', {
        ...manualProductPayload(properties),
        notify_channel: properties.notify_channel,
        failure_category: properties.failure_category
      }, { sourceSurface: 'notify_me' });
    },
    trackPickupOptionSelected(properties = {}) {
      return emit('hh_pickup_option_selected', {
        fulfillment_method: 'pickup',
        pickup_location_key: 'hideaway_hilo',
        checkout_stage: properties.checkout_stage,
        item_count_bucket: itemCountBucket(properties.item_count),
        cart_value_bucket: cartValueBucket(properties.cart_value_minor),
        currency: String(properties.currency || '').toUpperCase(),
        contains_owned_product: properties.contains_owned_product
      }, { sourceSurface: 'external_adapter' });
    },
    trackCheckoutHandoffStarted(properties = {}) {
      return emit('hh_checkout_handoff_started', {
        handoff_source: properties.handoff_source || 'other',
        item_count_bucket: itemCountBucket(properties.item_count),
        cart_value_bucket: cartValueBucket(properties.cart_value_minor),
        currency: String(properties.currency || '').toUpperCase(),
        contains_owned_product: properties.contains_owned_product,
        owned_item_count_bucket: itemCountBucket(properties.owned_item_count)
      }, { sourceSurface: 'external_adapter' });
    }
  });

  document.addEventListener('click', onDocumentClick, true);
  document.addEventListener('cart:item-added', trackConfirmedCartAdd);
  document.addEventListener('product:variant-change', (event) => {
    const source = document.querySelector(`variant-selects[data-section="${CSS.escape(String(event.detail?.sectionId || ''))}"]`);
    trackStockout(event.detail?.variant, 'shopper_selected_variant', source);
  });
  document.addEventListener('visitorConsentCollected', trackInitialPageState);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', trackInitialPageState);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackInitialPageState, { once: true });
  } else {
    trackInitialPageState();
  }
})();
